import type { ApiResponse, EventOrganizer } from '@eventflow/shared-types';
import type { Session } from '@supabase/supabase-js';
import { supabase } from './supabase';

export const API_BASE_URL =
  import.meta.env.VITE_API_URL || 'https://event-flow-server-psi.vercel.app/api';

// Caché de sesión en memoria para evitar múltiples llamadas a getSession()
let sessionCache: Session | null = null;
let sessionCacheTime = 0;
const CACHE_DURATION = 5000; // 5 segundos

// Flag para indicar que se está cerrando sesión (evita race conditions)
let isSigningOut = false;

function getJwtExpiry(token: string): number | null {
  const parts = token.split('.');
  if (parts.length < 2) return null;

  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const json = atob(padded);
    const payload = JSON.parse(json) as { exp?: number };
    return typeof payload.exp === 'number' ? payload.exp : null;
  } catch {
    return null;
  }
}

function isJwtExpired(token: string, skewSeconds = 30): boolean {
  const exp = getJwtExpiry(token);
  if (!exp) return false;
  const now = Math.floor(Date.now() / 1000);
  return exp <= now + skewSeconds;
}

// Función para actualizar el caché desde fuera (usado por AuthProvider)
export function updateSessionCache(session: Session | null) {
  sessionCache = session;
  sessionCacheTime = Date.now();

  // Resetear el flag de signing out cuando hay una sesión válida o se confirma que no hay sesión
  // Esto permite que las llamadas API funcionen correctamente después de un nuevo login
  isSigningOut = false;
}

// Función para limpiar completamente el caché (usado al cerrar sesión)
export function clearSessionCache() {
  sessionCache = null;
  sessionCacheTime = 0;
  isSigningOut = true;
}

// Función para verificar si se está cerrando sesión
export function isLoggingOut(): boolean {
  return isSigningOut;
}

/**
 * Limpieza completa de estado del cliente
 * - Caché de sesión en memoria
 * - LocalStorage (excepto preferencias de usuario)
 * - SessionStorage
 * - Cookies de Supabase
 */
export function clearAllClientState() {
  // 1. Limpiar caché en memoria
  clearSessionCache();

  // 2. Limpiar localStorage (claves relacionadas con auth/supabase)
  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (
      key &&
      (key.startsWith('sb-') || // Supabase keys
        key.includes('supabase') ||
        key.includes('auth') ||
        key.includes('session') ||
        key.includes('token'))
    ) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach((key) => {
    localStorage.removeItem(key);
  });

  // 3. Limpiar sessionStorage completamente
  sessionStorage.clear();

  // 4. Limpiar cookies de Supabase (si existen)
  document.cookie.split(';').forEach((cookie) => {
    const [name] = cookie.split('=');
    const trimmedName = name.trim();
    if (trimmedName.startsWith('sb-') || trimmedName.includes('supabase')) {
      document.cookie = `${trimmedName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
    }
  });
}

/**
 * Función robusta de cierre de sesión
 * Maneja errores de red y asegura limpieza completa
 */
export async function performSignOut(): Promise<{ success: boolean; error?: string }> {
  try {
    // 1. Marcar que estamos cerrando sesión (evita race conditions)
    isSigningOut = true;

    // 2. Intentar notificar al backend (best effort, no bloquea)
    try {
      const session = sessionCache;
      if (session?.access_token) {
        // Timeout corto para no bloquear si hay problemas de red
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 3000);

        await fetch(`${API_BASE_URL}/auth/logout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
          signal: controller.signal,
        }).catch(() => {
          // Ignorar errores de red - el logout local es suficiente
        });

        clearTimeout(timeoutId);
      }
    } catch {
      // Ignorar errores del backend - el logout local es lo importante
    }

    // 3. Limpiar todo el estado del cliente ANTES de llamar a Supabase
    clearAllClientState();

    // 4. Cerrar sesión en Supabase (esto también limpia su storage interno)
    const { error } = await supabase.auth.signOut({ scope: 'local' });

    if (error) {
      // No lanzar error - la limpieza local ya se hizo
    }

    return { success: true };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Error desconocido';

    // Aún así, intentar limpiar el estado local
    clearAllClientState();

    // Forzar limpieza de Supabase sin esperar respuesta
    supabase.auth.signOut({ scope: 'local' }).catch(() => { });

    return { success: false, error: errorMessage };
  }
}

/**
 * Cliente API con autenticación automática
 */
class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private async getAuthHeaders(): Promise<HeadersInit> {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    // Si se está cerrando sesión, no intentar obtener headers de auth
    if (isSigningOut) {
      return headers;
    }

    try {
      // Usar caché si está fresco
      const now = Date.now();
      if (sessionCache && now - sessionCacheTime < CACHE_DURATION) {
        if (sessionCache.access_token && !isJwtExpired(sessionCache.access_token)) {
          headers.Authorization = `Bearer ${sessionCache.access_token}`;
        } else if (sessionCache.access_token) {
          sessionCache = null;
          sessionCacheTime = 0;
        }
        return headers;
      }

      // Timeout para evitar que se cuelgue (2 segundos para producción)
      const timeoutPromise = new Promise<null>((resolve) => {
        setTimeout(() => resolve(null), 2000);
      });

      const sessionPromise = supabase.auth.getSession();

      const result = await Promise.race([sessionPromise, timeoutPromise]);

      if (result === null) {
        // Usar caché como fallback si está disponible
        if (sessionCache?.access_token && !isJwtExpired(sessionCache.access_token)) {
          headers.Authorization = `Bearer ${sessionCache.access_token}`;
        } else if (sessionCache?.access_token) {
          sessionCache = null;
          sessionCacheTime = 0;
        }
        return headers;
      }

      const {
        data: { session },
      } = result;

      // Actualizar caché
      sessionCache = session;
      sessionCacheTime = now;

      if (session?.access_token && !isJwtExpired(session.access_token)) {
        headers.Authorization = `Bearer ${session.access_token}`;
      } else if (session?.access_token) {
        sessionCache = null;
        sessionCacheTime = 0;
      }
    } catch {
      // Intentar usar caché como fallback
      if (sessionCache?.access_token && !isJwtExpired(sessionCache.access_token)) {
        headers.Authorization = `Bearer ${sessionCache.access_token}`;
      } else if (sessionCache?.access_token) {
        sessionCache = null;
        sessionCacheTime = 0;
      }
    }

    return headers;
  }

  async get<T>(endpoint: string): Promise<T> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: 'GET',
      headers,
    });
    return this.handleResponse<T>(response);
  }

  async post<T>(endpoint: string, body?: unknown): Promise<T> {
    // Para rutas públicas (auth), usar headers simples sin sesión
    const isPublicRoute = endpoint.startsWith('/auth/');
    const headers: HeadersInit = isPublicRoute
      ? { 'Content-Type': 'application/json' }
      : await this.getAuthHeaders();

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: 'POST',
      headers,
      body: body ? JSON.stringify(body) : undefined,
    });
    return this.handleResponse<T>(response);
  }

  async put<T>(endpoint: string, body?: unknown): Promise<T> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: 'PUT',
      headers,
      body: body ? JSON.stringify(body) : undefined,
    });

    return this.handleResponse<T>(response);
  }

  async patch<T>(endpoint: string, body?: unknown): Promise<T> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: 'PATCH',
      headers,
      body: body ? JSON.stringify(body) : undefined,
    });

    return this.handleResponse<T>(response);
  }

  async delete<T>(endpoint: string): Promise<T> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: 'DELETE',
      headers,
    });

    return this.handleResponse<T>(response);
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    let data: ApiResponse<T> | null = null;
    try {
      data = (await response.json()) as ApiResponse<T>;
    } catch {
      data = null;
    }

    if (!response.ok || !data?.success) {
      if (response.status === 401) {
        sessionCache = null;
        sessionCacheTime = 0;
        throw new Error('Tu sesión expiró. Vuelve a iniciar sesión e inténtalo de nuevo.');
      }

      const message =
        data?.error?.message ||
        (response.status
          ? `Error ${response.status}: ${response.statusText}`
          : 'Error en la solicitud');
      throw new Error(message);
    }

    return data.data as T;
  }
}

export const api = new ApiClient(API_BASE_URL);

// ============================================================================
// MÉTODOS API PARA GESTIÓN DE ORGANIZADORES DE EVENTOS
// ============================================================================

/**
 * Busca organizadores por email o nombre (para autocompletado)
 * @param searchTerm - Término de búsqueda (mínimo 2 caracteres)
 * @param eventId - (Opcional) Excluir organizadores que ya están en este evento
 */
export async function searchOrganizers(
  searchTerm: string,
  eventId?: string
): Promise<
  Array<{ id: string; email: string | null; organization_name: string; phone: string | null }>
> {
  const params = new URLSearchParams({ q: searchTerm });
  if (eventId) params.append('eventId', eventId);
  return api.get<
    Array<{ id: string; email: string | null; organization_name: string; phone: string | null }>
  >(`/organizers/search?${params.toString()}`);
}

/**
 * Obtiene la lista de organizadores de un evento
 */
export async function getEventOrganizers(eventId: string): Promise<EventOrganizer[]> {
  return api.get<EventOrganizer[]>(`/events/${eventId}/organizers`);
}

/**
 * Agrega un organizador a un evento (solo el creador puede hacerlo)
 */
export async function addEventOrganizer(eventId: string, userEmail: string): Promise<EventOrganizer> {
  return api.post<EventOrganizer>(`/events/${eventId}/organizers`, { user_email: userEmail });
}

/**
 * Quita un organizador de un evento (solo el creador puede hacerlo)
 */
export async function removeEventOrganizer(eventId: string, organizerId: string) {
  return api.delete(`/events/${eventId}/organizers/${organizerId}`);
}
